import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage    from './pages/auth/LoginPage';
import SetupWizard  from './pages/setup/SetupWizard';
import SetupGuard   from './guards/SetupGuard';
import AuthGuard    from './guards/AuthGuard';
import RoleGuard    from './guards/RoleGuard';
import PageGuard    from './guards/PageGuard';
import MultiSchoolGuard from './guards/MultiSchoolGuard';
import DashboardLayout from './layouts/DashboardLayout';
import IdleLogout   from './components/IdleLogout';
import InstallPrompt from './components/InstallPrompt';

import ProfilePage   from './pages/profile/ProfilePage';
import SettingsPage  from './pages/settings/SettingsPage';
import StudentPortalSetupPage from './pages/settings/StudentPortalSetupPage';
import UsersPage     from './pages/users/UsersPage';
import RolesPage     from './pages/roles/RolesPage';
import AuditPage     from './pages/audit/AuditPage';

import AdmissionsPage from './pages/admissions/AdmissionsPage';
import AdmissionDetailsPage from './pages/admissionDetails/AdmissionDetailsPage';
import AssignStudentAccountPage from './pages/studentAccounts/AssignStudentAccountPage';
import StudentBillsPage from './pages/studentBills/StudentBillsPage';
import PaymentsPage from './pages/payments/PaymentsPage';
import LedgersAndReversalsPage from './pages/ledgers/LedgersAndReversalsPage';
import CollectorSheetPage from './pages/collections/CollectorSheetPage';
import CollectorWeekSheetPage from './pages/collections/CollectorWeekSheetPage';
import ServiceListPage from './pages/collections/ServiceListPage';
import CollectionSettingsPage from './pages/collections/CollectionSettingsPage';
import PrepaymentDeskPage from './pages/collections/PrepaymentDeskPage';
import CollectorAssignmentsPage from './pages/collections/CollectorAssignmentsPage';
import DailySubscriptionsPage from './pages/collections/DailySubscriptionsPage';
import DailyCollectionPage from './pages/collections/DailyCollectionPage';
import SubscriptionBalancesPage from './pages/collections/SubscriptionBalancesPage';
import ScholarshipsPage from './pages/scholarships/ScholarshipsPage';
import CashbookPage from './pages/cashbook/CashbookPage';
import CashbookApprovalsPage from './pages/cashbook/CashbookApprovalsPage';
import ReportsPage    from './pages/reports/ReportsPage';
import ClassesPage   from './pages/classes/ClassesPage';
import CompletionPage from './pages/completion/CompletionPage';
import EmployeesPage from './pages/employees/EmployeesPage';
import PettyPage     from './pages/petty/PettyPage';
import PettyApprovalsPage from './pages/petty/PettyApprovalsPage';
import AssetsPage    from './pages/assets/AssetsPage';
import ScoresPage    from './pages/scores/ScoresPage';
import ExaminationHomePage     from './pages/examination/ExaminationHomePage';
import ExaminationSettingsPage from './pages/examination/ExaminationSettingsPage';
import RegisterSubjectPage     from './pages/examination/RegisterSubjectPage';
import ImportScoresPage        from './pages/examination/ImportScoresPage';
import StudentReportPage       from './pages/examination/StudentReportPage';
import GroupReportingPage      from './pages/examination/GroupReportingPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import HRDashboardPage from './pages/hr/HRDashboardPage';
import HRPayrollPage   from './pages/hr/HRPayrollPage';
import HRSettingsPage  from './pages/hr/HRSettingsPage';
import HRApprovalsPage from './pages/hr/HRApprovalsPage';
import MessagingPage   from './pages/messaging/MessagingPage';
import SchoolsPage     from './pages/schools/SchoolsPage';
import SchoolHubPage   from './pages/schools/SchoolHubPage';
import RegisterAssignmentPage from './pages/register/RegisterAssignmentPage';
import SchoolHubGuard  from './guards/SchoolHubGuard';

// Parent portal — separate experience from the staff app
import PortalLayout    from './portal/PortalLayout';
import PortalHome      from './portal/PortalHome';
import PortalAccount   from './portal/PortalAccount';
import PortalAcademics from './portal/PortalAcademics';
import PortalFinance   from './portal/PortalFinance';
import PortalProfile   from './portal/PortalProfile';
import ParentGuard     from './portal/ParentGuard';

// Teacher portal — minimal score-entry experience for teacher accounts
import TeacherPortalLayout      from './teacher/TeacherPortalLayout';
import TeacherPortalHub         from './teacher/TeacherPortalHub';
import TeacherPortalHome        from './teacher/TeacherPortalHome';
import TeacherPortalCollections from './teacher/TeacherPortalCollections';
// The collection sheet a teacher fills in: one collection, one class,
// PAID / CREDIT / NOT EATEN, and a summary of the round. Replaces the
// older multi-column day sheet, which is kept in the tree for now
// (teacher/TeacherDaySheet.jsx) until this has run a full term.
import TeacherCollectionSheet   from './teacher/TeacherCollectionSheet';
import TeacherPortalRegister    from './teacher/TeacherPortalRegister';
import TeacherGuard        from './teacher/TeacherGuard';

function ComingSoon({ title }) {
  return (
    <Box sx={{ p: 6, textAlign: 'center' }}>
      <Typography variant="h4" fontWeight={800} gutterBottom>{title}</Typography>
      <Typography variant="body1" color="text.secondary">Coming soon.</Typography>
    </Box>
  );
}

// Send collector-only users to their kiosk page when they hit /dashboard.

function DashboardOrCollect() {
  const { user } = useAuth();
  // Parents have no dashboard or sidebar — straight to the portal.
  if (user?.role === 'parent') return <Navigate to="/portal" replace />;
  // Teachers (role flagged is_teacher_role OR named teacher/teachers)
  // land on the dedicated score-entry portal — no staff sidebar.
  // Super-admins are excluded so they keep access to the full app.
  if (user?.is_teacher && user.role !== 'super_admin') {
    return <Navigate to="/teacher-portal" replace />;
  }
  const onlyCollector = user
    && user.role !== 'super_admin'
    && (user.pages || []).length === 1
    && (user.pages || [])[0] === 'collector_sheet';
  if (onlyCollector) return <Navigate to="/collect" replace />;
  return <DashboardPage />;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <InstallPrompt />
          <IdleLogout timeout={10 * 60 * 1000}>
          <Routes>
            <Route path="/setup" element={
              <SetupGuard mode="before"><SetupWizard /></SetupGuard>
            } />

            <Route path="/login" element={
              <SetupGuard mode="after"><LoginPage /></SetupGuard>
            } />

            {/* Parent portal — separate chrome (no admin sidebar). Parents
                land here straight from /login → /dashboard redirect. */}
            <Route path="/portal" element={
              <SetupGuard mode="after">
                <AuthGuard><ParentGuard><PortalLayout /></ParentGuard></AuthGuard>
              </SetupGuard>
            }>
              <Route index             element={<PortalHome />} />
              <Route path="account"    element={<PortalAccount />} />
              <Route path="academics"  element={<PortalAcademics />} />
              <Route path="finance"    element={<PortalFinance />} />
              <Route path="profile"    element={<PortalProfile />} />
            </Route>

            {/* Teacher portal — minimal score-entry chrome (no admin
                sidebar). Teachers land here straight from /login →
                /dashboard redirect when their role is flagged as a
                teacher role. */}
            <Route path="/teacher-portal" element={
              <SetupGuard mode="after">
                <AuthGuard><TeacherGuard><TeacherPortalLayout /></TeacherGuard></AuthGuard>
              </SetupGuard>
            }>
              {/* Hub landing — big cards that route into each area. */}
              <Route index element={<TeacherPortalHub />} />
              {/* Existing all-in-one score/remarks/report page, unchanged. */}
              <Route path="examination" element={<TeacherPortalHome />} />
              {/* One sheet for the whole day, across every income type the
                  collector is assigned to. A learner pays once and the
                  amount is split across what they're paying for, instead
                  of the collector flipping between a roster per type. */}
              <Route path="collections" element={<TeacherCollectionSheet />} />
              {/* The previous one-income-type-at-a-time roster, kept
                  reachable so nothing is lost if the day sheet needs
                  falling back on. */}
              <Route path="collections/by-type" element={<TeacherPortalCollections />} />
              {/* The morning roll call for the classes this teacher was
                  given on the Register Assignment page. */}
              <Route path="register" element={<TeacherPortalRegister />} />
            </Route>

            <Route element={
              <SetupGuard mode="after">
                <AuthGuard><DashboardLayout /></AuthGuard>
              </SetupGuard>
            }>
              <Route path="/dashboard" element={<DashboardOrCollect />} />
              <Route path="/profile"   element={<ProfilePage />} />

              {/* Super-admin only */}
              <Route path="/users"    element={<RoleGuard roles={['super_admin']}><UsersPage    /></RoleGuard>} />
              <Route path="/roles"    element={<RoleGuard roles={['super_admin']}><RolesPage    /></RoleGuard>} />
              <Route path="/audit"    element={<RoleGuard roles={['super_admin']}><AuditPage    /></RoleGuard>} />
              <Route path="/settings" element={<PageGuard pageKey="settings"><SettingsPage /></PageGuard>} />
              <Route path="/settings/parent-portal" element={<PageGuard pageKey="parent_portal"><StudentPortalSetupPage /></PageGuard>} />

              {/* Assignable modules */}
              <Route path="/admissions" element={<PageGuard pageKey="admissions"><AdmissionsPage /></PageGuard>} />
              <Route path="/classes"    element={<PageGuard pageKey="classes"><ClassesPage /></PageGuard>} />
              <Route path="/completion" element={<PageGuard pageKey="completion"><CompletionPage /></PageGuard>} />
              <Route path="/register-assignment" element={<PageGuard pageKey="register_assignment"><RegisterAssignmentPage /></PageGuard>} />
              <Route path="/scores"     element={<PageGuard pageKey="scores"><ScoresPage /></PageGuard>} />

              {/* Examination — ported from legacy ICMS main_window_1.java */}
              <Route path="/examination"                  element={<PageGuard pageKey="exam_home"><ExaminationHomePage /></PageGuard>} />
              <Route path="/examination/settings"         element={<PageGuard pageKey="exam_settings"><ExaminationSettingsPage /></PageGuard>} />
              <Route path="/examination/subjects"         element={<PageGuard pageKey="exam_subjects"><RegisterSubjectPage /></PageGuard>} />
              <Route path="/examination/import"           element={<PageGuard pageKey="exam_import"><ImportScoresPage /></PageGuard>} />
              <Route path="/examination/report/student"   element={<PageGuard pageKey="student_report"><StudentReportPage /></PageGuard>} />
              <Route path="/examination/report/group"     element={<PageGuard pageKey="group_report"><GroupReportingPage /></PageGuard>} />
              <Route path="/admission-details" element={<PageGuard pageKey="admission_details"><AdmissionDetailsPage /></PageGuard>} />
              <Route path="/assign-student-account" element={<PageGuard pageKey="assign_student_account"><AssignStudentAccountPage /></PageGuard>} />
              <Route path="/student-bills"          element={<PageGuard pageKey="student_bills"><StudentBillsPage /></PageGuard>} />
              <Route path="/payments" element={<PageGuard pageKey="payments"><PaymentsPage /></PageGuard>} />
              <Route path="/ledgers" element={<PageGuard pageKey="ledgers"><LedgersAndReversalsPage /></PageGuard>} />
              <Route path="/collect" element={<PageGuard pageKey="collector_sheet"><CollectorSheetPage /></PageGuard>} />
              {/* Approvals are the week sheet, and only the week sheet.
                  The old flat list is gone — it is not routed and there
                  is no way back to it. */}
              <Route path="/collections/approvals" element={<PageGuard pageKey="collector_approvals"><CollectorWeekSheetPage /></PageGuard>} />
              <Route path="/collections/approvals/list" element={<Navigate to="/collections/approvals" replace />} />
              <Route path="/collections/assignments" element={<PageGuard pageKey="collector_assignments"><CollectorAssignmentsPage /></PageGuard>} />
              <Route path="/collections/subscriptions" element={<PageGuard pageKey="daily_subscriptions"><DailySubscriptionsPage /></PageGuard>} />
              <Route path="/collections/daily" element={<PageGuard pageKey={['daily_collection','collector_sheet']}><DailyCollectionPage /></PageGuard>} />
              <Route path="/collections/balances" element={<PageGuard pageKey="subscription_balances"><SubscriptionBalancesPage /></PageGuard>} />
              {/* The service list is the first tab of Balances AND a page
                  of its own, so a cook can be given the serving list
                  without the school's arrears figures beside it. */}
              <Route path="/collections/service-list" element={<PageGuard pageKey={['service_list','subscription_balances']}><ServiceListPage /></PageGuard>} />
              <Route path="/collections/advance" element={<PageGuard pageKey={['collection_prepay','daily_subscriptions']}><PrepaymentDeskPage /></PageGuard>} />
              <Route path="/collections/settings" element={<PageGuard pageKey={['collection_settings','daily_subscriptions']}><CollectionSettingsPage /></PageGuard>} />
              <Route path="/scholarships" element={<PageGuard pageKey="scholarships"><ScholarshipsPage /></PageGuard>} />
              <Route path="/cashbook" element={<PageGuard pageKey="cashbook"><CashbookPage /></PageGuard>} />
              <Route path="/cashbook/approvals" element={<PageGuard pageKey="cashbook_approvals"><CashbookApprovalsPage /></PageGuard>} />
              <Route path="/petty"            element={<PageGuard pageKey="petty"><PettyPage /></PageGuard>} />
              <Route path="/petty/approvals"  element={<PageGuard pageKey="petty_approvals"><PettyApprovalsPage /></PageGuard>} />
              <Route path="/assets"           element={<PageGuard pageKey="assets"><AssetsPage /></PageGuard>} />
              <Route path="/employees"        element={<PageGuard pageKey="employees"><EmployeesPage /></PageGuard>} />

              {/* HR module (ported from TotalCare) */}
              <Route path="/hr"           element={<PageGuard pageKey="hr_dashboard"><HRDashboardPage /></PageGuard>} />
              <Route path="/hr/payroll"   element={<PageGuard pageKey="hr_payroll"><HRPayrollPage /></PageGuard>} />
              <Route path="/hr/settings"  element={<PageGuard pageKey="hr_settings"><HRSettingsPage /></PageGuard>} />
              <Route path="/hr/approvals" element={<PageGuard pageKey="hr_approvals"><HRApprovalsPage /></PageGuard>} />
              <Route path="/reports"   element={<ReportsPage   />} />
              <Route path="/messaging" element={<PageGuard pageKey="messaging"><MessagingPage /></PageGuard>} />

              {/* Multi-school (multi-tenant) — vendor-gated via
                  MultiSchoolGuard, which checks both the DB feature
                  flag AND the manage_schools permission. Invisible on
                  every deployment where the flag stays OFF. */}
              <Route path="/schools" element={<MultiSchoolGuard><SchoolsPage /></MultiSchoolGuard>} />
              {/* School Hub — pick a school and work in it. Gated on
                  "may open other schools" rather than on managing them,
                  so it appears for every main-school super administrator
                  but never for a super admin of another school. */}
              <Route path="/school-hub" element={<SchoolHubGuard><SchoolHubPage /></SchoolHubGuard>} />
            </Route>

            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </IdleLogout>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
